/**
 * AIComposeAssistant
 *
 * AI writing helper for compose: write with AI, smart reply chips,
 * tone adjuster, and subject suggester.
 */

import { useState, useCallback } from 'react';
import { Sparkles, Loader2, ChevronDown, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAICompose } from '../hooks/useAICompose';
import type { EmailAIReplySuggestion } from '@/lib/api';

interface AIComposeAssistantProps {
  accountId: string | undefined;
  replyToMessageId?: string | null;
  bodyHtml: string;
  subject: string;
  onSetBody: (html: string) => void;
  onSetSubject: (subject: string) => void;
}

const TONES = ['formal', 'casual', 'diplomatic', 'assertive', 'friendly'] as const;

export function AIComposeAssistant({
  accountId,
  replyToMessageId,
  bodyHtml,
  subject,
  onSetBody,
  onSetSubject,
}: AIComposeAssistantProps) {
  const {
    composing,
    aiCompose,
    replySuggestions,
    suggestingReplies,
    fetchReplySuggestions,
    adjustingTone,
    adjustTone,
    suggestingSubject,
    subjectSuggestions,
    suggestSubjects,
    clearSubjectSuggestions,
  } = useAICompose(accountId);

  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [previousBody, setPreviousBody] = useState<string | null>(null);

  // Write with AI
  const handleAICompose = useCallback(async () => {
    if (!prompt.trim()) return;
    const result = await aiCompose(prompt, {
      replyToMessageId: replyToMessageId || undefined,
    });
    if (result) {
      onSetBody(result.bodyHtml);
      if (result.subject && !subject) onSetSubject(result.subject);
    }
    setShowPrompt(false);
    setPrompt('');
  }, [prompt, aiCompose, replyToMessageId, subject, onSetBody, onSetSubject]);

  // Smart reply chips
  const handleFetchReplies = useCallback(() => {
    if (replyToMessageId) fetchReplySuggestions(replyToMessageId);
  }, [replyToMessageId, fetchReplySuggestions]);

  const handleSelectReply = useCallback(
    (suggestion: EmailAIReplySuggestion) => {
      onSetBody(suggestion.bodyHtml);
    },
    [onSetBody],
  );

  // Tone adjust
  const handleToneAdjust = useCallback(
    async (tone: (typeof TONES)[number]) => {
      if (!bodyHtml.trim()) return;
      setPreviousBody(bodyHtml);
      const adjusted = await adjustTone(bodyHtml, tone);
      if (adjusted) onSetBody(adjusted);
    },
    [bodyHtml, adjustTone, onSetBody],
  );

  const handleUndoTone = useCallback(() => {
    if (previousBody) {
      onSetBody(previousBody);
      setPreviousBody(null);
    }
  }, [previousBody, onSetBody]);

  // Subject suggest
  const handleSuggestSubject = useCallback(() => {
    const text = bodyHtml.replace(/<[^>]*>/g, '').trim();
    if (text) suggestSubjects(text);
  }, [bodyHtml, suggestSubjects]);

  return (
    <div className="space-y-2">
      {/* Smart reply chips */}
      {replyToMessageId && replySuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {replySuggestions.map((s, i) => (
            <Button
              key={i}
              variant="outline"
              size="sm"
              className="text-xs h-7 gap-1"
              onClick={() => handleSelectReply(s)}
            >
              <Sparkles className="h-3 w-3" /> {s.label}
            </Button>
          ))}
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-1 flex-wrap">
        {/* Write with AI */}
        {showPrompt ? (
          <div className="flex gap-1 flex-1 min-w-[200px]">
            <Input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want to say..."
              className="h-7 text-xs"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAICompose();
              }}
              autoFocus
            />
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleAICompose}
              disabled={composing || !prompt.trim()}
            >
              {composing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setShowPrompt(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setShowPrompt(true)}
          >
            <Sparkles className="h-3 w-3" /> Write with AI
          </Button>
        )}

        {/* Smart replies (for reply mode) */}
        {replyToMessageId && replySuggestions.length === 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={handleFetchReplies}
            disabled={suggestingReplies}
          >
            {suggestingReplies ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Lightbulb className="h-3 w-3" />
            )}
            Suggest replies
          </Button>
        )}

        {/* Tone adjuster */}
        {bodyHtml.trim() && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  disabled={adjustingTone}
                >
                  {adjustingTone ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Tone <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {TONES.map((tone) => (
                  <DropdownMenuItem
                    key={tone}
                    onClick={() => handleToneAdjust(tone)}
                    className="capitalize"
                  >
                    {tone}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {previousBody && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleUndoTone}>
                Undo
              </Button>
            )}
          </>
        )}

        {/* Subject suggester */}
        {!subject && bodyHtml.trim() && (
          <DropdownMenu
            onOpenChange={(open) => {
              if (open) handleSuggestSubject();
              if (!open) clearSubjectSuggestions();
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                disabled={suggestingSubject}
              >
                {suggestingSubject ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                Suggest subject
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {subjectSuggestions.length === 0 ? (
                <DropdownMenuItem disabled>Generating...</DropdownMenuItem>
              ) : (
                subjectSuggestions.map((s, i) => (
                  <DropdownMenuItem key={i} onClick={() => onSetSubject(s)}>
                    {s}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
